import { useSendNotification } from "@app/hooks/useNotification";
import { useAppRouter } from "@app/lib/platform";
import {
  useAgentTriggers,
  useDeleteTrigger,
} from "@app/lib/swr/agent_triggers";
import { useRoutines } from "@app/lib/swr/routines";
import { getRoutinesRoute } from "@app/lib/utils/router";
import { getTriggerDescription } from "@app/lib/utils/trigger_description";
import type { LightAgentConfigurationType } from "@app/types/assistant/agent";
import type { TriggerType } from "@app/types/assistant/triggers";
import { assertNever } from "@app/types/shared/utils/assert_never";
import type { WorkspaceType } from "@app/types/user";
import {
  ActionCard,
  Bell01,
  Button,
  CalendarCheck01,
  CardGrid,
  Clock,
  Dialog,
  DialogContainer,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Plus,
  Spinner,
} from "@dust-tt/sparkle";
import { useMemo, useState } from "react";

function getTriggerIcon(trigger: TriggerType) {
  switch (trigger.kind) {
    case "schedule":
      return Clock;
    case "event":
      return CalendarCheck01;
    case "webhook":
      return Bell01;
    default:
      assertNever(trigger);
  }
}

interface AgentTriggersTabProps {
  agentConfiguration: LightAgentConfigurationType;
  owner: WorkspaceType;
  onEditTrigger: (trigger: TriggerType) => void;
  onAddTrigger: () => void;
}

export function AgentTriggersTab({
  agentConfiguration,
  owner,
  onEditTrigger,
  onAddTrigger,
}: AgentTriggersTabProps) {
  const { triggers, isTriggersLoading } = useAgentTriggers({
    workspaceId: owner.sId,
    agentConfigurationId: agentConfiguration.sId,
  });

  const router = useAppRouter();
  const { templates } = useRoutines({ owner });
  // Catalog routines that are this agent's job; a hired agent's entries carry
  // only its name until install binds them to it.
  const catalogRoutines = useMemo(
    () =>
      templates.filter((template) =>
        template.agentSId
          ? template.agentSId === agentConfiguration.sId
          : template.agentName === agentConfiguration.name
      ),
    [templates, agentConfiguration.sId, agentConfiguration.name]
  );

  const [triggerToDelete, setTriggerToDelete] = useState<TriggerType | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const sendNotification = useSendNotification();

  const deleteTrigger = useDeleteTrigger({
    workspaceId: owner.sId,
    agentConfigurationId: agentConfiguration.sId,
  });

  const handleDeleteTrigger = async () => {
    if (!triggerToDelete) {
      return;
    }
    setIsDeleting(true);
    const success = await deleteTrigger(triggerToDelete.sId);
    setIsDeleting(false);
    setTriggerToDelete(null);

    if (success) {
      sendNotification({
        type: "success",
        title: "Routine deleted",
        description: `The routine "${triggerToDelete.name}" has been deleted.`,
      });
    } else {
      sendNotification({
        type: "error",
        title: "Failed to delete routine",
        description: "An error occurred while deleting the routine.",
      });
    }
  };

  // TODO(adrien): for now, we only show the user's triggers.
  // We might reconsider it, and display a "My routines" section,
  // and a "How others automate this" section in the future.
  const filteredTriggers = useMemo(
    () => triggers.filter((t) => t.isEditor),
    [triggers]
  );

  return (
    <>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">My routines</h3>
        <Button
          label="Add routine"
          icon={Plus}
          variant="outline"
          size="sm"
          onClick={onAddTrigger}
        />
      </div>

      {isTriggersLoading ? (
        <div className="w-full p-6">
          <Spinner variant="dark" />
        </div>
      ) : filteredTriggers.length === 0 ? (
        <div className="text-muted-foreground text-sm">
          You have no routines on this agent yet.
        </div>
      ) : (
        <CardGrid>
          {filteredTriggers.map((trigger) => (
            <ActionCard
              key={trigger.sId}
              icon={getTriggerIcon(trigger)}
              label={trigger.name}
              description={getTriggerDescription(trigger)}
              canAdd={false}
              disabled={trigger.status !== "enabled"}
              onClick={() => onEditTrigger(trigger)}
              onRemove={() => setTriggerToDelete(trigger)}
              cardContainerClassName="min-h-28"
            />
          ))}
        </CardGrid>
      )}

      {catalogRoutines.length > 0 && (
        <>
          <h3 className="pt-4 text-sm font-semibold">Can also run</h3>
          <CardGrid>
            {catalogRoutines.map((template) => (
              <ActionCard
                key={template.id}
                icon={template.kind === "event" ? CalendarCheck01 : Clock}
                label={template.name}
                description={template.tagline}
                canAdd={false}
                onClick={() =>
                  void router.push(
                    `${getRoutinesRoute(owner.sId)}?template=${template.id}`
                  )
                }
                cardContainerClassName="min-h-28"
              />
            ))}
          </CardGrid>
        </>
      )}

      <Dialog
        open={triggerToDelete !== null}
        onOpenChange={(open) => !open && setTriggerToDelete(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete routine</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the trigger "
              {triggerToDelete?.name}"?
            </DialogDescription>
          </DialogHeader>
          {isDeleting ? (
            <div className="flex justify-center py-8">
              <Spinner variant="dark" size="md" />
            </div>
          ) : (
            <>
              <DialogContainer>
                <b>This action cannot be undone.</b>
              </DialogContainer>
              <DialogFooter
                leftButtonProps={{
                  label: "Cancel",
                  variant: "outline",
                }}
                rightButtonProps={{
                  label: "Delete",
                  variant: "warning",
                  onClick: handleDeleteTrigger,
                }}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
